import { useEffect, useLayoutEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { geocodeZip } from "@/lib/geocodeZip";
import { clipCircleToLand } from "@/lib/landClip";

const TILE_URLS = {
    dark: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    light: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
};

const TYPE_COLOR = { team: "#ff4500", trainer: "#2196f3", event: "#22c55e", player: "#a855f7" };
const ICON_SVG = {
    team: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
    trainer: '<path d="M14.4 14.4 9.6 9.6"/><path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.768 1.768a2 2 0 1 1-2.828-2.829l6.364-6.364a2 2 0 1 1 2.828 2.829l-1.768 1.767a2 2 0 1 1 2.829 2.829z"/><path d="m21.5 21.5-1.4-1.4"/><path d="M3.9 3.9 2.5 2.5"/><path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z"/>',
    event: '<path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/>',
    player: '<circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/>',
};

// Resolve a marker's accent colour by its type (falls back to team orange).
const colorForType = (type) => TYPE_COLOR[type] || TYPE_COLOR.team;

// Single-listing marker: icon-only in the type colour, with a soft drop shadow (no circle).
function singleMarkerIcon(type) {
    const color = TYPE_COLOR[type] || TYPE_COLOR.team;
    return L.divIcon({
        className: "aau-marker-wrapper",
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.75))">${ICON_SVG[type] || ICON_SVG.team}</svg>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
    });
}
const markerIconTeam = singleMarkerIcon("team");
const markerIconTrainer = singleMarkerIcon("trainer");
const markerIconEvent = singleMarkerIcon("event");
const markerIconPlayer = singleMarkerIcon("player");

// Athlete marker with an in-range cue (concept C): a short line the same stroke width as
// the icon, tucked inside the bottom of the body — green when the athlete's travel radius
// reaches the searched ZIP, red when out of range. Falls back to the plain pin when the
// range is unknown (no ZIP entered).
function playerMarkerIcon(inRange) {
    if (inRange !== true && inRange !== false) return markerIconPlayer;
    const lineColor = inRange ? "#22c55e" : "#ef4444";
    return L.divIcon({
        className: "aau-marker-wrapper",
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#a855f7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,.75))">${ICON_SVG.player}<line x1="10" y1="18.5" x2="14" y2="18.5" stroke="${lineColor}" stroke-width="2" stroke-linecap="round"/></svg>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
    });
}

/** Google-style star rating row (partial gold fill), returns "" when no reviews */
function starRatingHtml(avg, count) {
    if (!count || count <= 0) return "";
    const pct = Math.max(0, Math.min(100, (Number(avg) / 5) * 100));
    return `
        <div style="display:flex;align-items:center;gap:5px;margin-top:4px">
            <span style="font-size:11px;color:#e5e5e5;font-weight:700">${Number(avg).toFixed(1)}</span>
            <span style="position:relative;display:inline-block;font-size:12px;line-height:1;letter-spacing:1px">
                <span style="color:#4a4a4a">★★★★★</span>
                <span style="position:absolute;top:0;left:0;white-space:nowrap;overflow:hidden;width:${pct}%;color:#fbbf24">★★★★★</span>
            </span>
            <span style="font-size:10px;color:#a0a0a0">(${count})</span>
        </div>`;
}

/** 48px left thumbnail for the marker card (logo/photo over a coloured-initial fallback) */
function markerThumbHtml(t) {
    const color = colorForType(t.type);
    const letter = ((t.name || "?").trim()[0] || "?").toUpperCase();
    const src = t.type === "event" ? t.photo : (t.logo || t.photo || "");
    const isLogoType = t.type === "team" || t.type === "trainer";
    const isEvent = t.type === "event";
    const fit = (isLogoType || isEvent) ? "contain" : "cover";
    const bg = t.preview_bg;
    const imgBg = isEvent ? "background:#000;padding:4px;"
        : bg === "black" ? "background:#000;padding:4px;"
        : bg === "clear" ? ""
        : (isLogoType ? "background:#fff;padding:4px;" : "");
    const fallback = `<span style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#0f0f0f;font-family:'Bebas Neue',sans-serif;font-size:20px;color:${color}">${letter}</span>`;
    const img = src
        ? `<img src="${src}" alt="" onerror="this.style.display='none'" style="position:absolute;inset:0;width:100%;height:100%;object-fit:${fit};${imgBg}display:block" />`
        : "";
    return `<span style="position:relative;display:block;width:48px;height:48px;border:1px solid #2a2a2a;overflow:hidden">${fallback}${img}</span>`;
}

/**
 * Compact marker card (design "#2"): logo on the left with the rounded distance
 * beneath it, and name → star rating → location → meta stacked on the right.
 * Width hugs its content. Pass opts.button to append a "View" action (popup).
 */
function markerCardHtml(t, opts = {}) {
    const distColor = t.in_range === false ? "#ef4444" : "#22c55e";
    const dist = t.distance_miles != null
        ? `<div style="color:${distColor};font-size:10px;font-weight:600;white-space:nowrap;margin-top:4px">${Math.round(t.distance_miles)} mi</div>`
        : "";
    const loc = `${t.city || ""}${t.state ? ", " + t.state : ""}${t.age_group ? " · " + t.age_group : ""}`;
    const metaColor = colorForType(t.type);
    let metaInner = "";
    if (t.type === "event") {
        const parts = [];
        if (t.division) parts.push(`<span style="color:${metaColor}">${t.division}</span>`);
        if (t.audience) parts.push(`<span style="color:#a0a0a0">${t.audience}</span>`);
        metaInner = parts.join(`<span style="color:#a0a0a0"> · </span>`);
    } else {
        const metaText = [t.division, (t.circuit ? t.circuit : "")].filter((s) => s && s.trim()).join(" · ");
        metaInner = metaText ? `<span style="color:${metaColor}">${metaText}</span>` : "";
    }
    const meta = metaInner ? `<div style="font-size:9.5px;text-transform:uppercase;letter-spacing:0.15em;margin-top:3px;white-space:nowrap">${metaInner}</div>` : "";
    const rating = starRatingHtml(t.avg_rating, t.review_count);
    const btn = opts.button
        ? `<button data-link="${opts.link}" class="aau-popup-btn" style="margin-top:8px;background:${opts.btnColor};color:#fff;border:0;padding:5px 10px;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;font-family:'DM Sans',sans-serif;cursor:pointer;font-weight:600">${opts.label}</button>`
        : "";
    return `
        <div style="display:inline-flex;gap:10px;align-items:flex-start;padding:8px 10px">
            <div style="display:flex;flex-direction:column;align-items:center;flex-shrink:0">
                ${markerThumbHtml(t)}
                ${dist}
            </div>
            <div style="display:flex;flex-direction:column">
                <div style="font-family:'Bebas Neue',sans-serif;font-size:18px;letter-spacing:0.04em;color:#fff;line-height:1.0;white-space:nowrap">${t.name || ""}${t.example ? ` <span style="font-family:'DM Sans',sans-serif;font-size:9px;font-weight:700;letter-spacing:0.14em;background:#ff4500;color:#fff;padding:1px 5px;border-radius:2px;vertical-align:middle">EXAMPLE</span>` : ""}</div>
                ${rating}
                <div style="color:#a0a0a0;font-size:11px;margin-top:3px;white-space:nowrap">${loc}</div>
                ${meta}
                ${btn}
            </div>
        </div>`;
}

/** Destination path for a marker's detail page */
function linkForTeam(t) {
    if (t.link) return t.link;
    if (t.type === "event") return `/events/${t.slug || t.id}`;
    if (t.type === "trainer") return `/trainers/${t.id}`;
    if (t.type === "player") return `/players/${t.id}`;
    return `/teams/${t.slug || t.id}`;
}

// --- Alaska & Hawaii insets -------------------------------------------------
// Real AK/HI sit far out in the Pacific, forcing the map to zoom way out. Instead we draw
// the actual state outlines inside two "inset" boxes at the lower-left (uniform-scaled to
// fit), and relocate listings there to their true spot WITHIN the state. Source boxes are
// the real geometry bounding boxes from us-states.geojson so shapes + markers stay aligned.
// True coordinates are kept on the listing for distance math; only the on-map pin moves.
const AK_SRC = { lngMin: -188.9, lngMax: -129.99, latMin: 51.61, latMax: 71.35 };
const HI_SRC = { lngMin: -159.76, lngMax: -154.81, latMin: 18.95, latMax: 22.23 };
const AK_DST = { latMin: 25.6, latMax: 31.6, lngMin: -123.8, lngMax: -114.2 };
const HI_DST = { latMin: 25.6, latMax: 29.0, lngMin: -113.0, lngMax: -106.6 };

// Uniform-scale + center a source bbox into a destination box. Returns (lat,lng) -> [lat,lng].
function makeFit(src, dst) {
    const sw = src.lngMax - src.lngMin, sh = src.latMax - src.latMin;
    const dw = dst.lngMax - dst.lngMin, dh = dst.latMax - dst.latMin;
    const scale = Math.min(dw / sw, dh / sh);
    const offX = dst.lngMin + (dw - sw * scale) / 2;
    const offY = dst.latMin + (dh - sh * scale) / 2;
    return (lat, lng) => [offY + (lat - src.latMin) * scale, offX + (lng - src.lngMin) * scale];
}
const AK_FIT = makeFit(AK_SRC, AK_DST);
const HI_FIT = makeFit(HI_SRC, HI_DST);

// Returns a relocated {lat,lng} for AK/HI points, or null for everything else.
function insetProject(lat, lng) {
    if (lat == null || lng == null) return null;
    if (lat >= 18 && lat <= 23.5 && lng >= -161.5 && lng <= -154) {
        const [la, ln] = HI_FIT(lat, lng);
        return { lat: la, lng: ln };
    }
    if (lat >= 50 && lng <= -129) {
        const [la, ln] = AK_FIT(lat, lng);
        return { lat: la, lng: ln };
    }
    return null;
}

// Deep-transform a GeoJSON geometry's coordinates ([lng,lat]) through a fit function.
function transformGeometry(geometry, fit) {
    const one = (c) => { const [la, ln] = fit(c[1], c[0]); return [ln, la]; };
    const walk = (c) => (typeof c[0] === "number" ? one(c) : c.map(walk));
    return { ...geometry, coordinates: walk(geometry.coordinates) };
}

// Marker position: inset box for AK/HI, true position otherwise.
function displayLatLng(t) {
    const p = insetProject(t.lat, t.lng);
    return p ? [p.lat, p.lng] : [t.lat, t.lng];
}

export default function MapView({ teams = [], center = [39.8283, -98.5795], zoom = 4, height = "600px", centerZip = "", radiusMiles = null, preciseCenter = null, onRequestLocation = null, linkResolver = null, fitToMarkers = false }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const clusterRef = useRef(null);
    const navigate = useNavigate();
    // Resolve a marker's destination. When a linkResolver is supplied and it returns a
    // falsy value (e.g. user-location pins with no detail page), the marker is non-clickable
    // but still shows the hover/tap preview card. Default = each listing's detail page.
    const linkResolverRef = useRef(linkResolver);
    linkResolverRef.current = linkResolver;
    const fitToMarkersRef = useRef(fitToMarkers);
    fitToMarkersRef.current = fitToMarkers;
    const prevFitRef = useRef(false);
    const resolveLink = useRef((t) => {
        const r = linkResolverRef.current;
        return r ? r(t) : linkForTeam(t);
    }).current;
    const { theme } = useTheme();
    const tileRef = useRef(null);
    const radiusLayerRef = useRef(null);
    const centerMarkerRef = useRef(null);
    const activeClusterRef = useRef(null);
    const navRef = useRef(navigate);
    const recenterRef = useRef(null);
    const recenterBtnRef = useRef(null);
    const noZipHandlerRef = useRef(null);
    const markersRef = useRef([]);
    const autoOpenRef = useRef(null);
    const applyFitRef = useRef(null);
    const [sheet, setSheet] = useState(null); // mobile bottom sheet: { items:[t], index }
    const [clusterPanel, setClusterPanel] = useState(null); // desktop cluster preview overlay: { items, x, y }
    const clusterCloseTimerRef = useRef(null);
    const cancelClusterCloseRef = useRef(() => {});
    const scheduleClusterCloseRef = useRef(() => {});
    noZipHandlerRef.current = onRequestLocation;

    // On mobile, shrink the map ~33% (too much empty ocean otherwise) — desktop keeps full height.
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
    );
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    const basePx = parseInt(height, 10) || 560;
    const effectiveHeight = isMobile ? `${Math.round(basePx * 0.67)}px` : height;
    // When the height changes, Leaflet needs to recompute its size + re-fit the radius view.
    useEffect(() => {
        if (mapRef.current) setTimeout(() => {
            if (!mapRef.current) return;
            if (applyFitRef.current) applyFitRef.current();
            else mapRef.current.invalidateSize();
        }, 60);
    }, [effectiveHeight]);
    navRef.current = navigate;

    useEffect(() => {
        if (!containerRef.current || mapRef.current) return;

        // USA tile coverage box (broad) vs. the tighter view the user is locked to.
        const TILE_BOUNDS = L.latLngBounds([[17.5, -180], [72, -64]]);
        // Continental view + AK/HI insets — snug to the US so the largest zoom-out just
        // frames the country/states with no empty ocean around the borders.
        const VIEW_BOUNDS = L.latLngBounds([[24.2, -125.3], [49.6, -66.3]]);
        // maxBounds must stay LARGER than the viewport at the fit/min zoom, otherwise
        // Leaflet loops forever in _panInsideMaxBounds trying to fit a bigger viewport
        // inside a smaller bounds ("Maximum call stack size exceeded"). Pad it out.
        const MAX_BOUNDS = VIEW_BOUNDS.pad(0.35);

        const map = L.map(containerRef.current, {
            center,
            zoom,
            minZoom: 2,
            maxZoom: 15,
            zoomSnap: 0.25,
            zoomControl: true,
            attributionControl: true,
            preferCanvas: true,
            maxBounds: MAX_BOUNDS,
            maxBoundsViscosity: 1.0,
            worldCopyJump: false,
        });

        tileRef.current = L.tileLayer(TILE_URLS[theme === "light" ? "light" : "dark"], {
            maxZoom: 16,
            attribution: 'Tiles &copy; Esri',
            bounds: TILE_BOUNDS,
            noWrap: true,
        }).addTo(map);

        mapRef.current = map;

        // Fit the whole country + AK/HI insets snugly into the viewport and pin the
        // minimum zoom to that fit, so the user can never zoom out past the US borders.
        const applyFit = () => {
            if (!mapRef.current) return;
            const m = mapRef.current;
            m.invalidateSize();
            const fitZoom = m.getBoundsZoom(VIEW_BOUNDS);
            m.setMinZoom(fitZoom);
            const focused = centerZip && /^\d{5}$/.test(centerZip);
            if (!focused) {
                m.fitBounds(VIEW_BOUNDS, { padding: [0, 0] });
            } else if (m.getZoom() < fitZoom) {
                m.setZoom(fitZoom);
            }
        };
        applyFitRef.current = applyFit;
        setTimeout(applyFit, 0);

        // Draw the AK / HI inset frames + labels.
        [["ALASKA", AK_DST], ["HAWAII", HI_DST]].forEach(([name, d]) => {
            L.rectangle([[d.latMin, d.lngMin], [d.latMax, d.lngMax]], {
                color: "#6a6e73", weight: 1, dashArray: "4 3",
                fill: true, fillColor: "#000", fillOpacity: 0.18, interactive: false,
            }).addTo(map);
            L.marker([d.latMax, d.lngMin], {
                interactive: false,
                keyboard: false,
                icon: L.divIcon({
                    className: "aau-inset-label",
                    html: `<span style="font-family:'Bebas Neue',sans-serif;font-size:11px;letter-spacing:.14em;color:#c7ccd1;white-space:nowrap;text-shadow:0 1px 2px rgba(0,0,0,.9)">${name}</span>`,
                    iconSize: [80, 14],
                    iconAnchor: [-2, 15],
                }),
            }).addTo(map);
        });

        // US state borders (consistent soft-gray outline at every zoom). Alaska & Hawaii are
        // drawn separately, remapped into their lower-left inset boxes.
        fetch("/us-states.geojson")
            .then((r) => r.json())
            .then((geo) => {
                if (!mapRef.current) return;
                const stateStyle = { color: "#6a6e73", weight: 1, opacity: 0.9, fill: false, smoothFactor: 0 };
                const isInset = (f) => ["Alaska", "Hawaii"].includes(String(f.properties && f.properties.name));
                const main = { type: "FeatureCollection", features: geo.features.filter((f) => !isInset(f)) };
                L.geoJSON(main, { interactive: false, style: stateStyle }).addTo(map);
                [["Alaska", AK_FIT], ["Hawaii", HI_FIT]].forEach(([nm, fit]) => {
                    const feat = geo.features.find((f) => String(f.properties && f.properties.name) === nm);
                    if (!feat) return;
                    L.geoJSON(transformGeometry(feat.geometry, fit), {
                        interactive: false,
                        style: { color: "#8a9096", weight: 1, opacity: 1, fill: true, fillColor: "#6a6e73", fillOpacity: 0.22, smoothFactor: 0 },
                    }).addTo(map);
                });
            })
            .catch(() => {});

        // Custom "recenter on zip code" control, placed under the zoom buttons.
        const RecenterControl = L.Control.extend({
            options: { position: "topleft" },
            onAdd: function () {
                const btn = L.DomUtil.create("a", "leaflet-bar leaflet-control leaflet-control-recenter");
                btn.href = "#";
                btn.title = "Recenter on zip code";
                btn.setAttribute("data-testid", "map-recenter-btn");
                btn.setAttribute("role", "button");
                btn.innerHTML = "<span style=\"display:flex;align-items:center;justify-content:center;width:30px;height:30px\"><span style=\"width:12px;height:12px;border-radius:50%;background:#2196f3;border:2px solid #fff;box-sizing:border-box;box-shadow:0 1px 3px rgba(0,0,0,0.5)\"></span></span>";
                recenterBtnRef.current = btn;
                L.DomEvent.on(btn, "click", (e) => {
                    L.DomEvent.stop(e);
                    // Blue "locate me" dot: always try to center on the visitor's real GPS
                    // location. Fall back to recentering on the active ZIP view if geolocation
                    // isn't wired up.
                    if (noZipHandlerRef.current) noZipHandlerRef.current();
                    else if (recenterRef.current) recenterRef.current();
                });
                L.DomEvent.disableClickPropagation(btn);
                return btn;
            },
        });
        map.addControl(new RecenterControl());
        clusterRef.current = L.markerClusterGroup({
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: false,
            zoomToBoundsOnClick: false,
            maxClusterRadius: 40,
            iconCreateFunction: (cluster) => {
                const children = cluster.getAllChildMarkers();
                const count = cluster.getChildCount();
                const counts = { team: 0, trainer: 0, event: 0, player: 0 };
                children.forEach((m) => {
                    const ty = m.__team && m.__team.type;
                    if (ty === "trainer") counts.trainer++;
                    else if (ty === "event") counts.event++;
                    else if (ty === "player") counts.player++;
                    else counts.team++;
                });
                const total = counts.team + counts.trainer + counts.event + counts.player || 1;
                // Proportional pie: each present type gets a slice sized by its share of the total.
                let acc = 0;
                const stops = [];
                [["team", TYPE_COLOR.team], ["trainer", TYPE_COLOR.trainer], ["event", TYPE_COLOR.event], ["player", TYPE_COLOR.player]].forEach(([k, c]) => {
                    if (counts[k] > 0) {
                        const start = (acc / total) * 100;
                        acc += counts[k];
                        const end = (acc / total) * 100;
                        stops.push(`${c} ${start}% ${end}%`);
                    }
                });
                const bg = stops.length > 1 ? `conic-gradient(${stops.join(", ")})` : (TYPE_COLOR[Object.keys(counts).find((k) => counts[k] > 0)] || TYPE_COLOR.team);
                const size = count >= 100 ? 48 : count >= 10 ? 42 : 38;
                const fs = count >= 100 ? 15 : 17;
                return L.divIcon({
                    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${bg};display:flex;align-items:center;justify-content:center;box-shadow:0 0 0 2px rgba(0,0,0,.35),0 0 10px rgba(0,0,0,.55)"><span style="font-family:'Bebas Neue',sans-serif;color:#fff;font-size:${fs}px;font-weight:700;text-shadow:0 1px 3px rgba(0,0,0,.95)">${count}</span></div>`,
                    className: "aau-cluster-wrapper",
                    iconSize: [size, size],
                    iconAnchor: [size / 2, size / 2],
                });
            },
        });
        map.addLayer(clusterRef.current);

        const hoverCapable = !!(window.matchMedia && window.matchMedia("(hover: hover)").matches);

        // Desktop cluster preview is a React overlay (ClusterPanel) rendered OUTSIDE Leaflet's
        // clipped map pane, so it can flip above/below the dot and grow past the map edge
        // without being cut off — and without the autoPan-vs-maxBounds recursion crash.
        const cancelClose = () => clearTimeout(clusterCloseTimerRef.current);
        const scheduleClose = () => {
            clearTimeout(clusterCloseTimerRef.current);
            clusterCloseTimerRef.current = setTimeout(() => { activeClusterRef.current = null; setClusterPanel(null); }, 180);
        };
        cancelClusterCloseRef.current = cancelClose;
        scheduleClusterCloseRef.current = scheduleClose;

        const openClusterPanel = (cluster) => {
            const items = cluster.getAllChildMarkers().map((m) => m.__team).filter(Boolean);
            if (!items.length) return;
            const pt = map.latLngToContainerPoint(cluster.getLatLng());
            activeClusterRef.current = cluster;
            setClusterPanel({ items, x: pt.x, y: pt.y });
        };

        if (hoverCapable) {
            // Desktop: open on hovering the dot; close when the pointer leaves the dot (bridged
            // by a short grace period so it can travel onto the panel).
            clusterRef.current.on("clustermouseover", (e) => { cancelClose(); openClusterPanel(e.layer); });
            clusterRef.current.on("clustermouseout", scheduleClose);
            // Keep the panel glued to its cluster while panning; a zoom rebuilds clusters so clear it.
            map.on("move", () => {
                const cl = activeClusterRef.current;
                if (!cl || typeof cl.getLatLng !== "function") return;
                const pt = map.latLngToContainerPoint(cl.getLatLng());
                setClusterPanel((prev) => (prev ? { ...prev, x: pt.x, y: pt.y } : prev));
            });
            map.on("zoomstart", () => { activeClusterRef.current = null; setClusterPanel(null); });
        } else {
            // Touch: tap the dot to open the bottom sheet (a leaflet popup gets clipped when
            // the map is crowded). The sheet lists everything here and shows the full card.
            clusterRef.current.on("clusterclick", (e) => {
                const items = e.layer.getAllChildMarkers().map((m) => m.__team).filter(Boolean);
                if (items.length) setSheet({ items, index: 0 });
            });
            map.on("click", () => setSheet(null));
        }

        return () => {
            map.remove();
            mapRef.current = null;
            clusterRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Swap map tiles to match the active theme (light tiles blend into light mode).
    useEffect(() => {
        if (tileRef.current) {
            tileRef.current.setUrl(TILE_URLS[theme === "light" ? "light" : "dark"]);
        }
    }, [theme]);

    // The blue dot always recenters on the visitor's live location.
    useEffect(() => {
        if (recenterBtnRef.current) {
            recenterBtnRef.current.title = "Center on my location";
        }
    }, [centerZip]);


    useEffect(() => {
        const map = mapRef.current;
        const cluster = clusterRef.current;
        if (!map || !cluster) return;

        cluster.clearLayers();

        const hoverCapable = !!(window.matchMedia && window.matchMedia("(hover: hover)").matches);
        markersRef.current = [];
        autoOpenRef.current = null;

        teams.forEach((t) => {
            const isTrainer = t.type === "trainer";
            const isEvent = t.type === "event";
            const isPlayer = t.type === "player";
            const icon = isEvent ? markerIconEvent : (isTrainer ? markerIconTrainer : (isPlayer ? playerMarkerIcon(t.in_range) : markerIconTeam));
            const marker = L.marker(displayLatLng(t), { icon });
            marker.__team = t;

            // Hover tooltip — desktop only. On touch, a tap fires a synthetic mouseover that
            // leaves this card stuck on the map, duplicating the bottom sheet, so skip it.
            if (hoverCapable) {
                marker.bindTooltip(markerCardHtml(t), {
                    direction: "top",
                    offset: [0, -8],
                    className: "aau-tooltip",
                    sticky: true,
                    opacity: 1,
                });
            }

            const linkPath = resolveLink(t);
            const label = isEvent ? "View Event" : (isTrainer ? "View Trainer" : (isPlayer ? "View Athlete" : "View Team"));
            const btnColor = colorForType(t.type);

            // Every marker gets the card popup (with a View button when it has a destination)
            // so it can auto-open when it becomes the only listing visible in the viewport.
            const popupHtml = markerCardHtml(t, linkPath ? { button: true, link: linkPath, label, btnColor } : {});
            marker.bindPopup(popupHtml, { className: "aau-popup", autoClose: false, autoPan: false });
            marker.on("popupopen", () => {
                setTimeout(() => {
                    const root = marker.getPopup && marker.getPopup() && marker.getPopup().getElement();
                    if (!root) return;
                    root.querySelectorAll(".aau-popup-btn").forEach((btn) => {
                        btn.onclick = (ev) => { ev.stopPropagation(); if (linkPath) navigate(linkPath); };
                    });
                    // Tapping anywhere on the map-attached card opens the detail page (when linkable).
                    const content = root.querySelector(".leaflet-popup-content");
                    if (content && linkPath) { content.style.cursor = "pointer"; content.onclick = () => navigate(linkPath); }
                }, 0);
            });

            if (hoverCapable) {
                // Mouse devices: the hover tooltip previews the card, so a click on the dot
                // goes straight to the detail page. Suppress Leaflet's default click-to-open
                // popup (we open popups programmatically for the sole-visible case instead).
                marker.off("click", marker._openPopup, marker);
                marker.on("click", () => { if (linkPath) navigate(linkPath); });
            } else {
                // Touch: tap the dot to open the SAME card attached to the map (like the desktop
                // hover card). Tapping the card then navigates to the detail page. Only one card
                // is open at a time.
                marker.off("click", marker._openPopup, marker);
                marker.on("click", () => {
                    markersRef.current.forEach((m) => { if (m !== marker) m.closePopup(); });
                    if (mapRef.current) mapRef.current.panTo(marker.getLatLng(), { animate: true });
                    marker.openPopup();
                });
            }

            marker.__link = linkPath;
            cluster.addLayer(marker);
            markersRef.current.push(marker);
        });

        // Open the card automatically whenever EXACTLY ONE individual marker is visible in the
        // current viewport (not clustered, in bounds). Closes it as soon as a 2nd point shows.
        const checkSoleVisible = () => {
            if (!mapRef.current || !clusterRef.current) return;
            if (!hoverCapable) return; // touch uses the bottom sheet, not auto-opened popups
            const b = mapRef.current.getBounds();
            const parents = new Set();
            let soleMarker = null;
            markersRef.current.forEach((m) => {
                const vp = clusterRef.current.getVisibleParent(m);
                if (!vp || typeof vp.getLatLng !== "function") return;
                if (!b.contains(vp.getLatLng())) return;
                parents.add(L.Util.stamp(vp));
                if (vp === m) soleMarker = m;  // an individual (non-clustered) marker
            });
            const onlyOne = parents.size === 1 && soleMarker;
            if (onlyOne) {
                if (autoOpenRef.current !== soleMarker) {
                    if (autoOpenRef.current) autoOpenRef.current.closePopup();
                    soleMarker.openPopup();
                    autoOpenRef.current = soleMarker;
                }
            } else if (autoOpenRef.current) {
                autoOpenRef.current.closePopup();
                autoOpenRef.current = null;
            }
        };

        if (fitToMarkersRef.current && markersRef.current.length > 0) {
            // State landing pages: frame just the filtered markers so the map "shows the state".
            try {
                const gb = L.featureGroup(markersRef.current).getBounds();
                if (gb && gb.isValid()) map.fitBounds(gb, { padding: [40, 40], maxZoom: 9, animate: false });
            } catch (e) { /* noop */ }
        } else if (!fitToMarkersRef.current && prevFitRef.current) {
            // Just left single-state focus (0 or 2+ states) → restore the normal US view.
            map.setView([39.8283, -98.5795], 4, { animate: false });
        } else if (teams.length === 1) {
            map.setView(displayLatLng(teams[0]), 10);
        }
        prevFitRef.current = fitToMarkersRef.current;

        map.on("moveend zoomend", checkSoleVisible);
        cluster.on("animationend", checkSoleVisible);
        const t0 = setTimeout(checkSoleVisible, 400);

        return () => {
            clearTimeout(t0);
            map.off("moveend zoomend", checkSoleVisible);
            cluster.off("animationend", checkSoleVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teams, navigate]);

    // Draw an expanding, water-clipped travel-radius circle around a searched ZIP.
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        let cancelled = false;

        const clearRadius = () => {
            if (radiusLayerRef.current) { map.removeLayer(radiusLayerRef.current); radiusLayerRef.current = null; }
            if (centerMarkerRef.current) { map.removeLayer(centerMarkerRef.current); centerMarkerRef.current = null; }
        };

        if (!centerZip || !/^\d{5}$/.test(centerZip)) { recenterRef.current = null; clearRadius(); return; }

        // Prefer the visitor's precise device location (from "use my location") as the center
        // point so a close zoom shows exactly where they are relative to listings; otherwise
        // fall back to the ZIP centroid.
        const usePrecise = preciseCenter && preciseCenter.lat != null && preciseCenter.lng != null;
        const pointPromise = usePrecise
            ? Promise.resolve({ lat: preciseCenter.lat, lng: preciseCenter.lng })
            : geocodeZip(centerZip);

        pointPromise.then(async (p) => {
            if (cancelled || !mapRef.current || !p) { if (!p) clearRadius(); return; }
            // Center marker — Google-Maps style living blue dot (white outline, pulsing)
            if (centerMarkerRef.current) map.removeLayer(centerMarkerRef.current);
            centerMarkerRef.current = L.marker([p.lat, p.lng], {
                icon: L.divIcon({
                    className: "aau-userdot",
                    html: '<div class="dot"></div>',
                    iconSize: [16, 16],
                    iconAnchor: [8, 8],
                }),
                interactive: false,
                keyboard: false,
            }).addTo(map);

            if (!radiusMiles) {
                if (radiusLayerRef.current) { map.removeLayer(radiusLayerRef.current); radiusLayerRef.current = null; }
                map.setView([p.lat, p.lng], 7, { animate: true });
                recenterRef.current = () => mapRef.current && mapRef.current.setView([p.lat, p.lng], 7, { animate: true });
                return;
            }
            const geom = await clipCircleToLand(p.lat, p.lng, radiusMiles);
            if (cancelled || !mapRef.current) return;
            if (radiusLayerRef.current) map.removeLayer(radiusLayerRef.current);
            radiusLayerRef.current = L.geoJSON(geom, {
                smoothFactor: 0,
                style: { color: "#ff4500", weight: 2, fillColor: "#ff4500", fillOpacity: 0.12 },
                interactive: false,
            }).addTo(map);
            // Fit the map to the CLIPPED land shape (not the full circle): on a coastline the
            // shape stops at the water's edge, so we don't waste the view on open ocean — the
            // coast becomes the border and the map keeps expanding over land as the radius grows.
            // Inland, the clipped shape ≈ the full circle, so the ZIP stays centered.
            let bounds = null;
            try {
                const b = radiusLayerRef.current.getBounds();
                if (b && b.isValid()) bounds = b;
            } catch { /* fall through */ }
            if (!bounds) {
                const latDelta = radiusMiles / 69;
                const lngDelta = radiusMiles / (69 * Math.max(0.2, Math.cos((p.lat * Math.PI) / 180)));
                bounds = [[p.lat - latDelta, p.lng - lngDelta], [p.lat + latDelta, p.lng + lngDelta]];
            }
            map.fitBounds(bounds, { padding: [12, 12], animate: true, duration: 0.4 });
            recenterRef.current = () => mapRef.current && mapRef.current.fitBounds(bounds, { padding: [12, 12], animate: true, duration: 0.4 });
        });

        return () => { cancelled = true; };
    }, [centerZip, radiusMiles, effectiveHeight, preciseCenter]);

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <div
                ref={containerRef}
                data-testid="usa-map"
                style={{ width: "100%", height: effectiveHeight }}
                className="border border-[#2a2a2a]"
            />
            {isMobile && sheet && sheet.items && sheet.items.length > 0 && (
                <MapSheet sheet={sheet} setSheet={setSheet} navigate={navigate} resolveLink={resolveLink} />
            )}
            {!isMobile && clusterPanel && (
                <ClusterPanel
                    panel={clusterPanel}
                    navigate={navigate}
                    resolveLink={resolveLink}
                    onMouseEnter={() => cancelClusterCloseRef.current()}
                    onMouseLeave={() => scheduleClusterCloseRef.current()}
                />
            )}
        </div>
    );
}

// Desktop cluster preview overlay — rendered in the map WRAPPER (outside Leaflet's clipped
// pane) so it can flip above/below the cluster dot and expand past the map edge without
// being cut off. Left: scrollable list of listings; right: live card for the hovered row.
function ClusterPanel({ panel, navigate, resolveLink, onMouseEnter, onMouseLeave }) {
    const ref = useRef(null);
    const [active, setActive] = useState(0);
    const [pos, setPos] = useState(null);
    const go = (t) => { const d = resolveLink ? resolveLink(t) : linkForTeam(t); if (d) navigate(d); };

    useEffect(() => { setActive(0); }, [panel.items]);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const h = el.offsetHeight;
        const w = el.offsetWidth;
        const gap = 16;
        const containerW = el.parentElement ? el.parentElement.clientWidth : w;
        // Flip below the dot only when there isn't enough room to open upward.
        const openDown = panel.y < h + gap + 6;
        const top = openDown ? panel.y + gap : panel.y - gap - h;
        let left = panel.x - w / 2;
        left = Math.max(6, Math.min(left, containerW - w - 6));
        setPos({ top, left });
    }, [panel, active]);

    const item = panel.items[Math.min(active, panel.items.length - 1)];

    return (
        <div
            ref={ref}
            data-testid="map-cluster-panel"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{
                position: "absolute",
                top: pos ? pos.top : panel.y,
                left: pos ? pos.left : panel.x,
                zIndex: 1000,
                visibility: pos ? "visible" : "hidden",
                background: "#141414",
                border: "1px solid #2a2a2a",
                boxShadow: "0 8px 24px rgba(255,69,0,0.15)",
                display: "flex",
                flexDirection: "column",
                maxWidth: "min(92vw, 520px)",
            }}
        >
            <div
                data-testid="map-cluster-panel-list"
                style={{ padding: "8px 10px 7px", borderBottom: "1px solid #2a2a2a", maxHeight: 190, overflowY: "auto" }}
            >
                <div style={{ color: "#ff4500", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 6, whiteSpace: "nowrap" }}>
                    {panel.items.length} here
                </div>
                {panel.items.map((t, i) => {
                    const dot = colorForType(t.type);
                    const rangeColor = t.in_range === false ? "#ef4444" : (t.in_range === true ? "#22c55e" : null);
                    return (
                        <div
                            key={t.id || i}
                            data-testid={`map-cluster-row-${t.id || i}`}
                            onMouseEnter={() => setActive(i)}
                            onClick={(e) => { e.stopPropagation(); go(t); }}
                            style={{ color: "#fff", fontSize: 12, padding: "3px 5px", lineHeight: 1.3, cursor: "pointer", whiteSpace: "nowrap", borderRadius: 2, background: i === active ? "rgba(255,69,0,0.14)" : "transparent" }}
                        >
                            <span style={{ color: dot, display: "inline-block", lineHeight: 1, borderBottom: rangeColor ? `2px solid ${rangeColor}` : "none", paddingBottom: rangeColor ? 1 : 0 }}>●</span>{" "}
                            <b style={{ fontWeight: 600 }}>{t.name || ""}</b>{" "}
                            <span style={{ color: "#a0a0a0", fontSize: 10 }}>— {[t.city, t.state].filter(Boolean).join(", ")}</span>
                        </div>
                    );
                })}
            </div>
            <div
                data-testid="map-cluster-panel-card"
                onClick={(e) => { e.stopPropagation(); go(item); }}
                style={{ cursor: "pointer", minWidth: 190, display: "flex", alignItems: "center" }}
                dangerouslySetInnerHTML={{ __html: markerCardHtml(item) }}
            />
        </div>
    );
}

// Mobile bottom sheet: a scrollable list of the tapped listings (top) and the full
// card for the selected one (bottom) — anchored to the map so it's never clipped.
function MapSheet({ sheet, setSheet, navigate, resolveLink }) {
    const { items, index } = sheet;
    const item = items[Math.min(index, items.length - 1)];
    const dotFor = (t) => (t.type === "event" ? "#22c55e" : t.type === "trainer" ? "#2196f3" : "#ff4500");
    const go = (t) => { const d = resolveLink ? resolveLink(t) : linkForTeam(t); if (d) navigate(d); };
    return (
        <div
            data-testid="map-mobile-sheet"
            className="absolute inset-x-0 bottom-0 z-[1000] bg-[#0f0f0f] border-t border-[#2a2a2a] flex flex-col shadow-[0_-10px_30px_rgba(0,0,0,0.6)]"
            style={{ maxHeight: "80%" }}
        >
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#2a2a2a] shrink-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">
                    {items.length > 1 ? `${items.length} listings here` : "Listing"}
                </div>
                <button data-testid="map-sheet-close" onClick={() => setSheet(null)} aria-label="Close" className="text-[#a0a0a0] hover:text-white h-6 w-6 inline-flex items-center justify-center">
                    <X className="h-4 w-4" />
                </button>
            </div>
            {items.length > 1 && (
                <div className="overflow-y-auto flex-1 min-h-0 divide-y divide-[#1e1e1e]" data-testid="map-sheet-list">
                    {items.map((t, i) => (
                        <button
                            key={t.id}
                            type="button"
                            data-testid={`map-sheet-row-${t.id}`}
                            onClick={() => setSheet({ items, index: i })}
                            className={`w-full text-left px-3 py-2 flex items-center gap-2 ${i === index ? "bg-[#ff4500]/15" : "hover:bg-[#1a1a1a]"}`}
                        >
                            <span style={{ color: dotFor(t) }}>●</span>
                            <span className="text-sm text-white truncate flex-1">{t.name}</span>
                            <span className="text-[10px] text-[#a0a0a0] truncate">{[t.city, t.state].filter(Boolean).join(", ")}</span>
                        </button>
                    ))}
                </div>
            )}
            <div
                data-testid="map-sheet-card"
                onClick={() => go(item)}
                className="shrink-0 border-t border-[#2a2a2a] cursor-pointer active:bg-[#141414]"
                dangerouslySetInnerHTML={{ __html: markerCardHtml(item) }}
            />
            <button
                type="button"
                data-testid="map-sheet-view-btn"
                onClick={() => go(item)}
                className="shrink-0 m-3 h-10 rounded-none text-xs uppercase tracking-[0.15em] font-semibold text-white"
                style={{ background: item.type === "event" ? "#22c55e" : item.type === "trainer" ? "#2196f3" : "#ff4500" }}
            >
                View {item.type === "event" ? "event" : item.type === "trainer" ? "trainer" : "team"}
            </button>
        </div>
    );
}
